/* WorkTriggerDetailsViewRenderer.js */

"use strict";

const GeneralTab = require("./tabs/worktriggeneraltab/WorkTriggerGeneralTabComponent");
const ScheduleTab = require("./tabs/worktrigscheduletab/WorkTriggerScheduleTabComponent");
const TargetsTab = require("./tabs/worktrigtargetstab/WorkTriggerTargetsTabComponent");
const TargetsReadonlyTab = require("./tabs/worktrigtargetsreadonlytab/WorkTriggerTargetsReadonlyTabComponent");
const WorkInputTab = require("./tabs/worktriginputtab/WorkTriggerWorkInputTabComponent");
const PlanTargetsReadonlyTab = require("./tabs/worktrigplantargetsreadonlytab/WorkTriggerPlanTargetsReadonlyTabComponent");

const _ = require("lodash");
const {managerForKey} = require("../../../../lib/managedSchedules");
_.mixin(require("underscore.string").exports());

class WorkTriggerDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("worktrig-details-view-gl");

        if (!this._model._isSystemTriggerType) {
            this._rootElem.component = this._model;
            this._contentElem = undefined;
            this._contentTabsElem = undefined;
            this._triggerType = undefined;
            this._tabComponents = {};
            this._tabElements = {};
            this._formDisabled = false;

            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Back",
                name: "BackToDetails",
                handler: (this._onBackToDetails = this._onBackToDetails.bind(this)),
            });
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Save",
                name: "SaveTriggerTypeDetails",
                handler: (this._onTriggerTypeDetailsSave =
                    this._onTriggerTypeDetailsSave.bind(this)),
            });
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Cancel",
                name: "CancelTriggerTypeDetails",
                handler: (this._onTriggerTypeDetailsCancel =
                    this._onTriggerTypeDetailsCancel.bind(this)),
            });
            this._model.gameEditor.hideViewAction("BackToDetails");
            this._registerListeners();
        } else {
            this._routeButtonContainer = document.createElement("worktrig-cisco-details-view-gl");
            this._routingButton = document.createElement("a");
            this._routingButton.setAttribute("class", "hyperlink");
            this._routeButtonContainer.appendChild(this._routingButton);
        }
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/

    _destroyObjects() {
        if (this._rootElem) {
            this._rootElem.remove();
            this._rootElem = undefined;
        }
        this._model = undefined;
    }

    shutdown() {
        return Promise.resolve()
            .then(() => {
                if (!this._model._isSystemTriggerType) {
                    this._unregisterListeners();
                    if (this._contentElem && this._contentElem.remove) {
                        this._contentElem.remove();
                    }
                    if (this._contentTabsElem && this._contentTabsElem.remove) {
                        this._contentTabsElem.remove();
                    }
                    this._destroyObjects();
                    this._contentElem = undefined;
                    this._contentTabsElem = undefined;
                    this._triggerType = undefined;
                    this._tabComponents = undefined;
                    this._tabElements = undefined;
                } else {
                    this._unregisterRoutingListener();
                    this._destroyObjects();
                    this._routingButton = undefined;
                    this._routeButtonContainer = undefined;
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }

    _registerListeners() {
        this._model.on(
            "EditorModeChanged",
            (this._onEditorModeChanged = this._onEditorModeChanged.bind(this))
        );
        this._model.on(
            "NotifTypeDefChanged",
            (this._onNotifTypeDefChanged = this._onNotifTypeDefChanged.bind(this))
        );
        this._model.on(
            "WorkTypeChanged",
            (this._onWorkTypeChanged = this._onWorkTypeChanged.bind(this))
        );
        this._model.on(
            "WorkTargetTypeChanged",
            (this._onWorkTargetTypeChanged = this._onWorkTargetTypeChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("EditorModeChanged", this._onEditorModeChanged);
        this._model.removeListener("NotifTypeDefChanged", this._onNotifTypeDefChanged);
        this._model.removeListener("WorkTypeChanged", this._onWorkTypeChanged);
        this._model.removeListener("WorkTargetTypeChanged", this._onWorkTargetTypeChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._model._isSystemTriggerType) {
            if (this._rootElem.hasChildNodes()) {
                return this._renderContent().then(() => {
                    this._renderTabs();
                });
            }
            return Promise.all([
                this._model.whenTriggerTypeIsReady(),
                this._model.whenWorkTypeIsReady(),
            ])
                .then(() => {
                    if (this._model._triggerType.isExhausted) {
                        this._disableViewActions();
                    }
                    this._triggerType = this._model.getTriggerType();
                    return this._renderContent();
                })
                .then(() => {
                    this._renderTabs();
                    // TODO Check why this header has been introduced
                    // let header = document.createElement('div');
                    // header.classList.add('worktrig-details-header');
                    // this._rootElem.appendChild(header);
                    this._rootElem.appendChild(this._contentElem);
                    // this._rootElem.appendChild(this._contentTabsElem);
                });
        } else {
            let routingButtonLabel;
            if (this._model._triggerWorkType === "retrieveSupportData") {
                routingButtonLabel = "Manage Support Data schedule";
            }
            if (this._model._triggerWorkType === "runDataRetention") {
                routingButtonLabel = `Manage Data Retention schedule`;
            }
            if (this._model._triggerWorkType === "dataSync") {
                routingButtonLabel = `Manage Data Sync schedule`;
            }
            if (this._model._triggerWorkType === "networkDiscovery") {
                routingButtonLabel = `Manage Network Discovery schedule`;
            }
            if (this._model._triggerWorkType === "commitAndPush") {
                routingButtonLabel = `Manage Commit and Push schedule`;
            }
            const manager = managerForKey(this._model._managedBy);
            if (manager) {
                routingButtonLabel = `Manage ${manager.scheduleName} schedule`;
            }
            this._routingButton.innerHTML = routingButtonLabel;
            this._registerRoutingListener();
            this._rootElem.appendChild(this._routeButtonContainer);
        }
    }

    _registerRoutingListener() {
        this._routingButton.addEventListener(
            "click",
            (this._routeToScheduleMgtView = this._routeToScheduleMgtView.bind(this))
        );
    }

    _unregisterRoutingListener() {
        this._routingButton.removeEventListener("click", this._routeToScheduleMgtView);
    }

    _routeToScheduleMgtView() {
        const manager = managerForKey(this._model._managedBy);
        if (manager) {
            window.location.href = window.location.origin + manager.path;
            return;
        }
        if (this._model._triggerWorkType === "networkDiscovery") {
            let rd = {};
            const networkDiscoveryPlanId = _.get(this._model, "_triggerType.workTarget.id");
            if (networkDiscoveryPlanId) {
                rd = {
                    openEditor: true,
                    selection: [networkDiscoveryPlanId],
                };
            }
            this._model._router.goTo({
                app: "GameConsole",
                s: "GameCenter",
                sln: "DeviceManager",
                exp: "network_discovery",
                rd,
            });
        } else {
            let sr;
            if (
                this._model._triggerWorkType === "retrieveSupportData" ||
                this._model._triggerWorkType === "commitAndPush"
            ) {
                sr = "Organization/Integrations";
            }
            if (this._model._triggerWorkType === "runDataRetention") {
                sr = "Organization/DataRetention";
            }
            if (this._model._triggerWorkType === "dataSync") {
                sr = "Organization/CMDBIntegration";
            }
            this._model._router.goTo({
                app: "GameConsole",
                s: "Settings",
                sln: "Settings",
                sr,
            });
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */

    /*******************************************/
    _onTriggerTypeDetailsSave() {
        if (this.model.isNotificationsBlockValid) {
            this._disableViewActions();
            this._hideViewActions();
            return this._model.saveTriggerType().then(() => {
                this._enableViewActions();
            });
        } else {
            this.model.gameEditor._modalUserFeedback.displayError({
                errorMsg:
                    "The notification recipient list must contain valid emails separated by commas",
            });
        }
    }

    _onTriggerTypeDetailsCancel() {
        this._hideViewActions();
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onTriggerTypeDetailsCancelAction =
                this._onTriggerTypeDetailsCancelAction.bind(this))
        );
    }

    _onTriggerTypeDetailsCancelAction() {
        this._model.resetTriggerTypeProperties();
        _.keys(this._tabComponents).forEach((tabCompName) => {
            if (tabCompName !== "targets") {
                this._tabComponents[tabCompName].shutdown();
            }
        });
        this._tabComponents = [];
        return this.render();
    }

    _onEditorModeChanged() {
        this.render();
    }

    _onNotifTypeDefChanged() {
        this._setScheduleTabVisibility(false);
    }

    _onWorkTypeChanged(workType) {
        this._setWorkInputTabVisibility(_.get(workType, "workInputs"));
    }

    _onWorkTargetTypeChanged(workTargetType) {
        this._setTargetTabVisibility(workTargetType);
    }

    _onBackToDetails() {
        this._model._gameEditor._titleCmpnt.setTitle("Details");
        this._model.emit("BackFromTarget");
        this._model.setEditorMode("general");
        this._model._targetViewOpen = undefined;
        if (this._model._triggerType.isDirty()) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
        this._model.gameEditor.setBreadCrumbs([]);
        this._model.gameEditor.hideViewAction("BackToDetails");
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */

    /*******************************************/
    _renderTabs() {
        // if(!this._contentTabsElem){
        //     this._contentTabsElem = document.createElement('worktrig-details-content-tabs');
        //     let tabNames = ['general','workDetails','targets'];
        //     tabNames.forEach((tabName)=>{
        //         let tabElem = document.createElement('worktrig-details-content-tab');
        //         tabElem.dataset.editorMode = _.underscored(tabName);
        //         this._tabElements[tabElem.dataset.editorMode] = tabElem;
        //         tabElem.innerText=_.titleize(_.humanize(tabName));
        //         tabElem.addEventListener('click',(e)=>{
        //             let eMode = e.target.dataset.editorMode;
        //             this._model.setEditorMode(eMode);
        //         });
        //         this._contentTabsElem.appendChild(tabElem);
        //     });
        //     let schedVis = _.get(this._model, 'triggerType.triggerType') === 'SCHEDULE';
        //     this._setScheduleTabVisibility(schedVis);
        //     this._setTargetTabVisibility(this._model.mapWorkTargetToTargetType(this._model.workTarget));
        //     this._setWorkInputTabVisibility(_.get(this._model.getWorkType(), 'workInputs'));
        // }
        // //change selected tab
        // let currentSelectedTab = this._contentTabsElem.querySelector('worktrig-details-content-tab.selected');
        // if(currentSelectedTab){
        //     currentSelectedTab.classList.remove('selected');
        // }
        // let newSelectedTab = this._contentTabsElem.querySelector('worktrig-details-content-tab[data-editor-mode='+this._model.editorMode+']');
        // newSelectedTab.classList.add('selected');
    }

    _renderContent() {
        let tabName = this._model.editorMode;
        let tabComp = this._tabComponents[tabName];
        return Promise.resolve()
            .then(() => {
                if (!this._contentElem) {
                    this._contentElem = document.createElement("worktrig-details-content");
                }
                if (!tabComp || tabName === "targets") {
                    switch (tabName) {
                        case "schedule":
                            this._model._gameEditor._titleCmpnt.setTitle("Details");
                            tabComp = new ScheduleTab(this._model);
                            break;
                        case "targets":
                            this._model._gameEditor._titleCmpnt.setTitle("Targets");
                            tabComp = new TargetsTab(this._model);
                            break;
                        case "targetsReadMode":
                            this._model._gameEditor._titleCmpnt.setTitle("Targets");
                            tabComp = new TargetsReadonlyTab(this._model);
                            break;
                        case "planTargetsReadMode":
                            this._model._gameEditor._titleCmpnt.setTitle("Targets");
                            tabComp = new PlanTargetsReadonlyTab(this._model);
                            break;
                        case "work_details":
                            this._model._gameEditor._titleCmpnt.setTitle("Details");
                            tabComp = new WorkInputTab(this._model);
                            break;
                        default:
                            this._model._gameEditor._titleCmpnt.setTitle("Details");
                            tabComp = new GeneralTab(this._model);
                            break;
                    }
                    this._tabComponents[tabName] = tabComp;
                    return tabComp.renderer.render();
                } else {
                    return tabComp.renderer.render();
                }
            })
            .then(() => {
                this._contentElem.innerHTML = "";
                this._contentElem.appendChild(tabComp.renderer.rootElement);
            });
    }

    _setScheduleTabVisibility(vis) {
        if (this._tabElements.schedule) {
            if (vis) {
                this._tabElements.schedule.classList.remove("hidden");
            } else {
                this._tabElements.schedule.classList.add("hidden");
            }
        }
    }

    _setTargetTabVisibility(workTargetType) {
        if (workTargetType && this._tabElements.targets) {
            if (workTargetType === "SPECIFIC") {
                this._tabElements.targets.classList.remove("hidden");
            } else {
                this._tabElements.targets.classList.add("hidden");
            }
        }
    }

    _setWorkInputTabVisibility(workInputs) {
        if (workInputs && workInputs.length > 1) {
            this._tabElements.work_details.classList.remove("hidden");
        } else {
            this._tabElements.work_details.classList.add("hidden");
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /* START PRIVATE ACTION METHODS */

    /*******************************************/

    _showViewActions() {
        if (_.isUndefined(this._model._targetViewOpen)) {
            this._model.gameEditor.showViewAction("SaveTriggerTypeDetails");
            this._model.gameEditor.showViewAction("CancelTriggerTypeDetails");
        }
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveTriggerTypeDetails");
        this._model.gameEditor.hideViewAction("CancelTriggerTypeDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveTriggerTypeDetails");
        this._model.gameEditor.disableViewAction("CancelTriggerTypeDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveTriggerTypeDetails");
        this._model.gameEditor.enableViewAction("CancelTriggerTypeDetails");
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /*******************************************/
}

module.exports = WorkTriggerDetailsViewRenderer;
